import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { formatDate } from './utils/dateFormatter';
import { useAccessGroups } from './ResourcesRouter.js';
import {
	Button,
	Input,
	Select,
	SelectItem,
	Chip,
	Skeleton,
	Table,
	TableHeader,
	TableBody,
	TableColumn,
	TableRow,
	TableCell,
	addToast
} from '@heroui/react';
import { Plus, Edit, Trash2, X, ClipboardList } from 'lucide-react';
import DiscrepanciesApi from '../../api/discrepanciesApi.js';
import OrganizationApi from '../../api/organizationApi.js';
import ResourcesApi from '../../api/resourcesApi.js';
import HeroUIBreadcrumb from '../breadcrumbs/HeroUIBreadcrumb.js';
import { DiscrepancyDetailModal, DiscrepancyFormModal } from './DiscrepanciesModal.js';

// Get organization unique from URL
const getOrgUnique = () => {
	const match = window.location.pathname.match(/\/org\/([^/]+)\//);
	return match ? match[1] : '';
};

export const PRIORITY_OPTIONS = [
	{ value: '1', label: 'High', color: 'danger' },
	{ value: '2', label: 'Medium', color: 'warning' },
	{ value: '3', label: 'Low', color: 'default' }
];

export const STATUS_OPTIONS = [
	{ value: 'new', label: 'New', color: 'primary' },
	{ value: 'open', label: 'Open', color: 'danger' },
	{ value: 'inprogress', label: 'In Progress', color: 'warning' },
	{ value: 'deferred', label: 'Deferred', color: 'secondary' },
	{ value: 'closed', label: 'Closed', color: 'success' }
];

const STATUS_FILTERS = [
	{ value: 'all', label: 'All' },
	{ value: 'open', label: 'Open' },
	{ value: 'new', label: 'New' },
	{ value: 'inprogress', label: 'In Progress' },
	{ value: 'deferred', label: 'Deferred' },
	{ value: 'closed', label: 'Closed' }
];

function DiscrepanciesPage() {
	// Get aircraftId from React Router params
	const { aircraftId: aircraftIdParam } = useParams();
	const navigate = useNavigate();
	const accessGroups = useAccessGroups();
	const aircraftIdFromState = useSelector(state => state.discrepancies?.aircraftId);

	// Use aircraftId from params (primary), fallback to state
	const aircraftId = aircraftIdParam ? parseInt(aircraftIdParam, 10) : aircraftIdFromState;

	const [aircraft, setAircraft] = React.useState(null);
	const [discrepancies, setDiscrepancies] = React.useState([]);
	const [mechanics, setMechanics] = React.useState([]);
	const [loading, setLoading] = React.useState(true);
	const [filterStatus, setFilterStatus] = React.useState('all');
	const [sortDescriptor, setSortDescriptor] = React.useState({
		column: 'date',
		direction: 'descending'
	});

	// Modal states
	const [isFormModalOpen, setIsFormModalOpen] = React.useState(false);
	const [isDetailModalOpen, setIsDetailModalOpen] = React.useState(false);
	const [selectedDiscrepancy, setSelectedDiscrepancy] = React.useState(null);
	const [formMode, setFormMode] = React.useState('add'); // 'add' or 'edit'
	const [formData, setFormData] = React.useState({
		name: '',
		description: '',
		priority: '2',
		assignTo: '',
		logbookEntry: ''
	});
	const [saving, setSaving] = React.useState(false);
	const [newComment, setNewComment] = React.useState('');

	// Fetch aircraft data
	React.useEffect(() => {
		const fetchAircraft = async () => {
			if (!aircraftId) return;
			try {
				const response = await ResourcesApi.getAircraft(aircraftId);
				const data = response.data?.data || response.data || response;
				setAircraft(data);
			} catch (err) {
				console.error('Failed to load aircraft:', err);
			}
		};
		fetchAircraft();
	}, [aircraftId]);

	// Fetch discrepancies
	const fetchDiscrepancies = React.useCallback(async () => {
		if (!aircraftId) return;
		setLoading(true);
		try {
			const response = await DiscrepanciesApi.getAircraftDiscrepancies(aircraftId);
			const data =
				response.data?.success && response.data?.data
					? response.data.data
					: Array.isArray(response.data)
						? response.data
						: [];
			setDiscrepancies(data);
		} catch (err) {
			console.error('Failed to load discrepancies:', err);
			addToast({ title: 'Error', description: 'Failed to load discrepancies', color: 'danger' });
		} finally {
			setLoading(false);
		}
	}, [aircraftId]);

	// Fetch mechanics
	const fetchMechanics = React.useCallback(async () => {
		try {
			const response = await OrganizationApi.getOrganizationMechanics();
			const data =
				response.data?.success && response.data?.data
					? response.data.data
					: Array.isArray(response.data)
						? response.data
						: [];
			setMechanics(data);
		} catch (err) {
			console.error('Failed to load mechanics:', err);
		}
	}, []);

	// Initial fetch
	React.useEffect(() => {
		if (aircraftId) {
			fetchDiscrepancies();
			fetchMechanics();
		}
	}, [aircraftId, fetchDiscrepancies, fetchMechanics]);

	// Filter and sort discrepancies
	const filteredDiscrepancies = React.useMemo(() => {
		let result = [...discrepancies];

		// Filter by status
		if (filterStatus !== 'all') {
			result = result.filter(d => d.status === filterStatus);
		}

		// Sort
		result.sort((a, b) => {
			const { column, direction } = sortDescriptor;
			let aVal = a[column];
			let bVal = b[column];

			if (column === 'date') {
				aVal = aVal || 0;
				bVal = bVal || 0;
			} else if (column === 'priority') {
				aVal = parseInt(aVal, 10) || 3;
				bVal = parseInt(bVal, 10) || 3;
			} else if (column === 'assignTo') {
				aVal = getMechanicName(aVal) || '';
				bVal = getMechanicName(bVal) || '';
			} else {
				aVal = (aVal || '').toString().toLowerCase();
				bVal = (bVal || '').toString().toLowerCase();
			}

			let comparison = 0;
			if (aVal < bVal) comparison = -1;
			if (aVal > bVal) comparison = 1;

			return direction === 'descending' ? -comparison : comparison;
		});

		return result;
	}, [discrepancies, filterStatus, sortDescriptor]);

	// Helpers
	const getMechanicName = assignTo => {
		if (!assignTo) return null;
		const mechanicId = typeof assignTo === 'object' ? assignTo.id : assignTo;
		const mechanic = mechanics.find(m => m.id === mechanicId);
		return mechanic ? `${mechanic.name} ${mechanic.surname || ''}`.trim() : null;
	};

	const getPriorityInfo = priority => {
		return PRIORITY_OPTIONS.find(o => o.value === String(priority)) || PRIORITY_OPTIONS[2];
	};

	const getStatusInfo = status => {
		return STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[1];
	};


	// Count by status
	const statusCounts = React.useMemo(() => {
		const counts = { all: discrepancies.length };
		STATUS_OPTIONS.forEach(s => {
			counts[s.value] = discrepancies.filter(d => d.status === s.value).length;
		});
		return counts;
	}, [discrepancies]);

	// Handlers
	const handleAdd = () => {
		setFormData({ name: '', description: '', priority: '2', assignTo: '', logbookEntry: '' });
		setFormMode('add');
		setIsFormModalOpen(true);
	};

	const handleEdit = discrepancy => {
		setSelectedDiscrepancy(discrepancy);
		setFormData({
			name: discrepancy.name || '',
			description: discrepancy.description || '',
			priority: String(discrepancy.priority || '2'),
			assignTo: discrepancy.assignTo?.id ? String(discrepancy.assignTo.id) : '',
			logbookEntry: discrepancy.logbookEntry || ''
		});
		setFormMode('edit');
		setIsFormModalOpen(true);
	};

	const handleViewDetail = async discrepancy => {
		setSelectedDiscrepancy(discrepancy);
		setIsDetailModalOpen(true);
		try {
			const response = await DiscrepanciesApi.getDiscrepancy(discrepancy.id);
			const data =
				response.data?.success && response.data?.data ? response.data.data : response.data;
			setSelectedDiscrepancy(data);
		} catch (err) {
			console.error('Failed to load discrepancy details:', err);
		}
	};

	const handleSave = async () => {
		if (!formData.name.trim()) {
			addToast({ title: 'Error', description: 'Name is required', color: 'danger' });
			return;
		}
		if (!formData.description.trim()) {
			addToast({ title: 'Error', description: 'Description is required', color: 'danger' });
			return;
		}

		setSaving(true);
		try {
			const payload = {
				name: formData.name,
				description: formData.description,
				priority: parseInt(formData.priority, 10),
				assignTo: formData.assignTo ? parseInt(formData.assignTo, 10) : null,
				logbookEntry: formData.logbookEntry,
				aircraftId: parseInt(aircraftId, 10)
			};

			if (formMode === 'add') {
				await DiscrepanciesApi.addDiscrepancy(payload);
				addToast({ title: 'Success', description: 'Discrepancy added', color: 'success' });
			} else {
				await DiscrepanciesApi.updateDiscrepancy(selectedDiscrepancy.id, payload);
				addToast({ title: 'Success', description: 'Discrepancy updated', color: 'success' });
			}

			await fetchDiscrepancies();
			setIsFormModalOpen(false);
		} catch (err) {
			const message = err.response?.data?.error?.[0] || err.message || 'Failed to save';
			addToast({ title: 'Error', description: message, color: 'danger' });
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async discrepancy => {
		if (!window.confirm(`Delete discrepancy "${discrepancy.name}"?`)) return;

		try {
			await DiscrepanciesApi.deleteDiscrepancy(discrepancy.id);
			addToast({ title: 'Success', description: 'Discrepancy deleted', color: 'success' });
			await fetchDiscrepancies();
			setIsDetailModalOpen(false);
		} catch (err) {
			const message = err.response?.data?.error?.[0] || err.message || 'Failed to delete';
			addToast({ title: 'Error', description: message, color: 'danger' });
		}
	};

	const handleChangeStatus = async (discrepancy, newStatus) => {
		try {
			await DiscrepanciesApi.changeStatus(discrepancy.id, { status: newStatus });
			addToast({
				title: 'Success',
				description: `Status changed to ${newStatus}`,
				color: 'success'
			});
			await fetchDiscrepancies();
			if (selectedDiscrepancy?.id === discrepancy.id) {
				setSelectedDiscrepancy({ ...selectedDiscrepancy, status: newStatus });
			}
		} catch (err) {
			const message = err.response?.data?.error?.[0] || err.message || 'Failed to change status';
			addToast({ title: 'Error', description: message, color: 'danger' });
		}
	};

	const handleAddComment = async () => {
		if (!newComment.trim() || !selectedDiscrepancy) return;

		try {
			await DiscrepanciesApi.addComment(selectedDiscrepancy.id, { disposition: newComment });
			addToast({ title: 'Success', description: 'Comment added', color: 'success' });
			setNewComment('');
			const response = await DiscrepanciesApi.getDiscrepancy(selectedDiscrepancy.id);
			const data =
				response.data?.success && response.data?.data ? response.data.data : response.data;
			setSelectedDiscrepancy(data);
		} catch (err) {
			const message = err.response?.data?.error?.[0] || err.message || 'Failed to add comment';
			addToast({ title: 'Error', description: message, color: 'danger' });
		}
	};

	const handleBackToAircraft = () => {
		navigate(`/aircraft/${aircraftId}`);
	};

	const orgUnique = getOrgUnique();
	const breadcrumbCustomItems = [
		{ label: window.location.pathname.includes('/hangars') ? 'Aircraft Status' : 'Resources', href: `/org/${orgUnique}/${window.location.pathname.includes('/hangars') ? 'hangars' : 'resources'}/` },
		{
			label: aircraft?.name || 'Aircraft',
			href: `/org/${orgUnique}/resources/viewAircraft/${aircraftId}/`
		}
	];


	// Skeleton rows for loading state
	const renderSkeletonRows = () => {
		return Array.from({ length: 5 }).map((_, index) => (
			<TableRow key={`skeleton-${index}`}>
				<TableCell>
					<Skeleton className="h-4 w-48 rounded" />
				</TableCell>
				<TableCell>
					<Skeleton className="h-4 w-24 rounded" />
				</TableCell>
				<TableCell>
					<Skeleton className="h-6 w-16 rounded-full" />
				</TableCell>
				<TableCell>
					<Skeleton className="h-4 w-32 rounded" />
				</TableCell>
				<TableCell>
					<Skeleton className="h-6 w-20 rounded-full" />
				</TableCell>
				<TableCell>
					<div className="flex justify-end gap-1">
						<Skeleton className="h-8 w-8 rounded-lg" />
						<Skeleton className="h-8 w-8 rounded-lg" />
						<Skeleton className="h-8 w-8 rounded-lg" />
					</div>
				</TableCell>
			</TableRow>
		));
	};

	return (
		<div className="max-w-[1400px] mx-auto px-4 py-8">
			<HeroUIBreadcrumb currentPage="Discrepancies" customItems={breadcrumbCustomItems} />

			{/* Page Header */}
			<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
				<div className="flex items-center gap-4">
					<div className="p-3 rounded-full bg-gradient-to-br from-rose-500 to-red-600">
						<ClipboardList className="w-8 h-8 text-white" />
					</div>
					<div>
						<h1 className="text-2xl font-bold">{aircraft?.name || 'Aircraft'}</h1>
						<p className="text-default-500 text-sm">
							{aircraft?.registrationNumber}
							{(aircraft?.manufacturer || aircraft?.model) && ' · '}
							{[aircraft?.manufacturer, aircraft?.model].filter(Boolean).join(' ')}
							{' · Discrepancies'}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-3 self-end md:self-auto">
					{(accessGroups._loaded && accessGroups.discrepanciesAdd) && (
						<Button color="primary" startContent={<Plus className="w-4 h-4" />} onPress={handleAdd}>
							Add Discrepancy
						</Button>
					)}
					<Button variant="light" isIconOnly onPress={handleBackToAircraft}>
						<X className="w-5 h-5" />
					</Button>
				</div>
			</div>

			{/* Status Filter Chips */}
			<div className="flex gap-2 mb-6 flex-wrap">
				{STATUS_FILTERS.map(filter => {
					const count = statusCounts[filter.value] || 0;
					const isSelected = filterStatus === filter.value;
					return (
						<Chip
							key={filter.value}
							variant={isSelected ? 'solid' : 'flat'}
							color={isSelected ? 'primary' : 'default'}
							className="cursor-pointer"
							onClick={() => setFilterStatus(filter.value)}
							endContent={
								<span
									className={`ml-1 min-w-5 h-5 px-1.5 rounded-full text-xs font-medium inline-flex items-center justify-center ${
										isSelected
											? 'bg-primary-foreground/20 text-primary-foreground'
											: 'bg-default-200 text-default-600'
									}`}
								>
									{count}
								</span>
							}
						>
							{filter.label}
						</Chip>
					);
				})}
			</div>

			{/* Table */}
			{loading ? (
				<Table aria-label="Loading discrepancies" isStriped>
					<TableHeader>
						<TableColumn>NAME</TableColumn>
						<TableColumn>DATE</TableColumn>
						<TableColumn>PRIORITY</TableColumn>
						<TableColumn>ASSIGNED TO</TableColumn>
						<TableColumn>STATUS</TableColumn>
						<TableColumn align="end">
							{(accessGroups._loaded && (accessGroups.discrepanciesManage || accessGroups.discrepanciesDelete)) ? 'ACTIONS' : ''}
						</TableColumn>
					</TableHeader>
					<TableBody>{renderSkeletonRows()}</TableBody>
				</Table>
			) : filteredDiscrepancies.length === 0 ? (
				<div className="text-center py-12 text-default-400">
					{discrepancies.length === 0 ? 'No discrepancies found' : 'No discrepancies match filter'}
				</div>
			) : (
				<Table
					aria-label="Discrepancies table"
					isStriped
					sortDescriptor={sortDescriptor}
					onSortChange={setSortDescriptor}
				>
					<TableHeader>
						<TableColumn key="name" allowsSorting>
							NAME
						</TableColumn>
						<TableColumn key="date" allowsSorting>
							DATE
						</TableColumn>
						<TableColumn key="priority" allowsSorting>
							PRIORITY
						</TableColumn>
						<TableColumn key="assignTo" allowsSorting>
							ASSIGNED TO
						</TableColumn>
						<TableColumn key="status" allowsSorting>
							STATUS
						</TableColumn>
						<TableColumn key="actions" align="end">
							{(accessGroups._loaded && (accessGroups.discrepanciesManage || accessGroups.discrepanciesDelete)) ? 'ACTIONS' : ''}
						</TableColumn>
					</TableHeader>
					<TableBody>
						{filteredDiscrepancies.map(d => {
							const priority = getPriorityInfo(d.priority);
							const status = getStatusInfo(d.status);
							const assigneeName = getMechanicName(d.assignTo);

							return (
								<TableRow key={d.id}>
									<TableCell>
										<button
											className="text-left hover:text-primary hover:underline font-medium"
											onClick={() => handleViewDetail(d)}
										>
											{d.name}
										</button>
										{d.description && (
											<p className="text-xs text-default-400 truncate max-w-xs mt-1">
												{d.description}
											</p>
										)}
									</TableCell>
									<TableCell>{formatDate(d.date)}</TableCell>
									<TableCell>
										<Chip size="sm" color={priority.color} variant="flat">
											{priority.label}
										</Chip>
									</TableCell>
									<TableCell>{assigneeName || '-'}</TableCell>
									<TableCell>
										{(accessGroups._loaded && accessGroups.discrepanciesStatus) ? (
											<Select
												aria-label="Status"
												size="sm"
												selectedKeys={[d.status]}
												onSelectionChange={keys => {
													const newStatus = Array.from(keys)[0];
													if (newStatus && newStatus !== d.status) {
														handleChangeStatus(d, newStatus);
													}
												}}
												classNames={{
													trigger: 'min-h-8 h-8 min-w-28',
													value: 'text-xs'
												}}
											>
												{STATUS_OPTIONS.map(opt => (
													<SelectItem key={opt.value}>{opt.label}</SelectItem>
												))}
											</Select>
										) : (
											<Chip size="sm" variant="flat" color={STATUS_OPTIONS.find(s => s.value === d.status)?.color || 'default'}>
												{STATUS_OPTIONS.find(s => s.value === d.status)?.label || d.status}
											</Chip>
										)}
									</TableCell>
									<TableCell>
										{(accessGroups._loaded && (accessGroups.discrepanciesManage || accessGroups.discrepanciesDelete)) && (
											<div className="flex gap-1 justify-end">
												{accessGroups.discrepanciesManage && (
													<Button isIconOnly size="sm" variant="light" onPress={() => handleEdit(d)}>
														<Edit className="w-4 h-4" />
													</Button>
												)}
												{accessGroups.discrepanciesDelete && (
													<Button
														isIconOnly
														size="sm"
														variant="light"
														color="danger"
														onPress={() => handleDelete(d)}
													>
														<Trash2 className="w-4 h-4" />
													</Button>
												)}
											</div>
										)}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			)}

			{/* Modals */}
			<DiscrepancyFormModal
				isOpen={isFormModalOpen}
				onClose={() => setIsFormModalOpen(false)}
				formMode={formMode}
				formData={formData}
				setFormData={setFormData}
				saving={saving}
				mechanics={mechanics}
				handleSave={handleSave}
			/>
			<DiscrepancyDetailModal
				isOpen={isDetailModalOpen}
				onClose={() => setIsDetailModalOpen(false)}
				selectedDiscrepancy={selectedDiscrepancy}
				accessGroups={accessGroups}
				newComment={newComment}
				setNewComment={setNewComment}
				handleChangeStatus={handleChangeStatus}
				handleAddComment={handleAddComment}
				handleDelete={handleDelete}
				handleEdit={handleEdit}
				getPriorityInfo={getPriorityInfo}
				getStatusInfo={getStatusInfo}
				getMechanicName={getMechanicName}
			/>
		</div>
	);
}

export default DiscrepanciesPage;
